import { getDramaCover, getDramaTitle, normalizeStatus, resolveAssetUrl } from '@/pages/drama-review/utils';

import type { DramaItem } from '@/services/dramas';
import type { BannerEditorItem, TheaterBannerConfig, TheaterBannerConfigItem } from '@/types/theater-banner';

import { THEATER_BANNER_MAX_ITEMS } from '@/constants/theater-banner';

import {
    extractMiniDramaAssetPath,
    resolveMiniDramaCdnUrl,
    resolveMiniDramaDisplayUrl,
} from '@/utils/mini-drama-assets';

/** 旧配置里这些字段出现过，说明 thumbUrl 是剧集图快照，不是运营上传 */
const LEGACY_ITEM_KEYS = [
    'title',
    'description',
    'bannerUrl',
    'customBannerUrl',
    'customThumbUrl',
    'previewHlsUrl',
    'previewVideoUrl',
    'customPreviewVideoUrl',
    'tags',
    'creatorName',
    'totalEpisodes',
    'totalPlayCount',
    'totalHeatValue',
    'avgRating',
    'actorAvatarUrls',
] as const;

const resolveDramaListAssetUrl = (path?: string): string => resolveAssetUrl(path);

/** 运营上传的图转成写入配置的 CDN URL */
const toCdnUrl = (url?: string): string => {
    if (!url?.trim()) return '';
    return resolveMiniDramaCdnUrl(extractMiniDramaAssetPath(url));
};

const readNonEmptyString = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed || null;
};

/**
 * 旧配置会把剧集封面抄进 thumbUrl / bannerUrl，运营上传的图在 customThumbUrl / customBannerUrl。
 * 新配置只认 thumbUrl；没有运营图时为 null。
 */
const readOpsThumbUrl = (item: Record<string, unknown>): string | null => {
    const customThumbUrl = readNonEmptyString(item.customThumbUrl) ?? readNonEmptyString(item.customBannerUrl);
    if (customThumbUrl) return customThumbUrl;

    const isLegacy = LEGACY_ITEM_KEYS.some((key) => {
        const value = item[key];
        if (value == null || value === '') return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
    });
    if (isLegacy) return null;

    return readNonEmptyString(item.thumbUrl);
};

export const defaultBannerConfig = (): TheaterBannerConfig => ({
    enabled: false,
    items: [],
});

/**
 * 保存时校验：
 * - 无剧集 → 强制关闭（不允许空 Banner 开着）
 * - 有剧集 → 尊重运营手动开关
 */
export const resolveBannerEnabled = (itemCount: number, enabled?: boolean) => itemCount > 0 && Boolean(enabled);

export const parseBannerConfigValue = (value: unknown): TheaterBannerConfig => {
    if (!value || typeof value !== 'object') return defaultBannerConfig();

    const raw = value as { enabled?: unknown; items?: unknown };
    const enabled = typeof raw.enabled === 'boolean' ? raw.enabled : true;
    const items = Array.isArray(raw.items) ? raw.items : [];
    const parsedItems = items
        .flatMap((item, index) => {
            if (!item || typeof item !== 'object') return [];
            const record = item as Record<string, unknown>;
            if (!record.dramaId) return [];

            return [
                {
                    dramaId: String(record.dramaId),
                    sortOrder: typeof record.sortOrder === 'number' ? record.sortOrder : index,
                    thumbUrl: readOpsThumbUrl(record),
                },
            ];
        })
        .sort((a, b) => a.sortOrder - b.sortOrder);

    return {
        enabled: resolveBannerEnabled(parsedItems.length, enabled),
        items: parsedItems,
    };
};

export const hydrateBannerItems = (
    configItems: TheaterBannerConfigItem[],
    dramaMap: Map<string, DramaItem>,
): BannerEditorItem[] =>
    configItems.map((item, index) => {
        const drama = dramaMap.get(item.dramaId);

        return {
            dramaId: item.dramaId,
            sortOrder: index,
            drama: drama ?? null,
            missing: !drama,
            thumbUrl: item.thumbUrl,
        };
    });

export const serializeBannerItem = (item: BannerEditorItem, sortOrder: number): TheaterBannerConfigItem => {
    const thumbUrl = toCdnUrl(item.thumbUrl ?? undefined);

    return {
        dramaId: item.dramaId,
        sortOrder,
        thumbUrl: thumbUrl || null,
    };
};

export const serializeBannerItems = (items: BannerEditorItem[]): TheaterBannerConfigItem[] =>
    items.map((item, index) => serializeBannerItem(item, index));

export const buildBannerConfigValue = (
    items: BannerEditorItem[],
    settings: Pick<TheaterBannerConfig, 'enabled'>,
): TheaterBannerConfig => ({
    enabled: resolveBannerEnabled(items.length, settings.enabled),
    items: serializeBannerItems(items),
});

/** 运营自定义图；空字符串视为未配置 */
export const resolveCustomThumbUrl = (item: BannerEditorItem): string => {
    const raw = item.thumbUrl?.trim();
    if (!raw) return '';
    return resolveMiniDramaDisplayUrl(raw);
};

/** 剧集封面，仅用于后台预览，不写入配置 */
export const resolveDramaCoverUrl = (drama?: DramaItem | null): string =>
    resolveDramaListAssetUrl(drama ? getDramaCover(drama) : undefined);

export const hasCustomThumbImage = (item: BannerEditorItem) => Boolean(item.thumbUrl?.trim());

/** 剧集列表 status 为 DELETED 时表示用户已删除 */
export const isDramaDeleted = (drama?: DramaItem | null) => {
    if (!drama) return false;
    return normalizeStatus(drama.status ?? drama.audit_status) === 'DELETED';
};

/** 已下架：不宜继续作为客户端 Banner */
export const isDramaOffline = (drama?: DramaItem | null) => {
    if (!drama) return false;
    return normalizeStatus(drama.status ?? drama.audit_status) === 'OFFLINE';
};

/** 已配置 Banner 需要从配置中移除的剧集状态 */
export const isBannerDramaUnavailable = (drama?: DramaItem | null) => isDramaDeleted(drama) || isDramaOffline(drama);

const shouldPruneBannerItem = (item: BannerEditorItem, dramaMap: Map<string, DramaItem>, selectedResolved: boolean) => {
    const drama = dramaMap.get(item.dramaId);
    if (drama && isBannerDramaUnavailable(drama)) return true;
    // 已按「已选 ID」逐条校验仍找不到 → 列表侧已不可见，从配置移除
    if (selectedResolved && !drama) return true;
    return false;
};

/**
 * 用剧集详情合并已选 Banner。
 * - dramas 应是「与已选相关」的剧集（按 ID 校验结果，或当前页里命中的已选项）
 * - selectedResolved=true：表示已完成按 ID 校验，找不到/不可用的项可安全 prune
 */
export const mergeEditorItemsWithCatalog = (
    items: BannerEditorItem[],
    dramas: DramaItem[],
    options?: { selectedResolved?: boolean; /** @deprecated 使用 selectedResolved */ catalogComplete?: boolean },
): BannerEditorItem[] => {
    const dramaMap = new Map(dramas.map((drama) => [drama.id, drama]));
    const selectedResolved = options?.selectedResolved ?? options?.catalogComplete ?? false;

    return items
        .filter((item) => !shouldPruneBannerItem(item, dramaMap, selectedResolved))
        .map((item, index) => {
            const drama = dramaMap.get(item.dramaId);
            if (!drama) {
                return {
                    ...item,
                    sortOrder: index,
                    // 未完成 ID 校验时不要误标 missing（当前页不含该剧很正常）
                    missing: selectedResolved ? true : item.missing,
                };
            }

            return {
                ...item,
                sortOrder: index,
                drama,
                missing: false,
            };
        });
};

export const resolveBannerTitle = (item: BannerEditorItem): string => {
    if (item.drama) return getDramaTitle(item.drama);
    return item.dramaId;
};

export const canAddBannerItem = (items: BannerEditorItem[]) => items.length < THEATER_BANNER_MAX_ITEMS;

export const addBannerItem = (items: BannerEditorItem[], drama: DramaItem): BannerEditorItem[] => {
    if (items.length >= THEATER_BANNER_MAX_ITEMS) return items;
    if (items.some((entry) => entry.dramaId === drama.id)) return items;

    return [
        ...items,
        {
            dramaId: drama.id,
            sortOrder: items.length,
            drama,
            missing: false,
            thumbUrl: null,
        },
    ];
};

export const removeBannerItem = (items: BannerEditorItem[], dramaId: string): BannerEditorItem[] =>
    items.filter((item) => item.dramaId !== dramaId).map((item, index) => ({ ...item, sortOrder: index }));

export const updateBannerItem = (
    items: BannerEditorItem[],
    dramaId: string,
    patch: Partial<BannerEditorItem>,
): BannerEditorItem[] => items.map((item) => (item.dramaId === dramaId ? { ...item, ...patch } : item));

export const moveBannerItem = (
    items: BannerEditorItem[],
    index: number,
    direction: 'up' | 'down',
): BannerEditorItem[] => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return items;

    const next = [...items];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    return next.map((item, sortIndex) => ({ ...item, sortOrder: sortIndex }));
};
